import { useEffect, useRef, type RefObject } from "react";

import { MapBrowserEvent, Map as MapOlType } from "ol";
import VectorSource from "ol/source/Vector";
import { featuresPolygons } from "../helpers";
import { AreaPolygonType } from "@/blocks/warnings-map/types/area-polygon-type";
import { DataWeatherStation } from "@/schemas/observation-schema";
import VectorLayer from "ol/layer/Vector";

export const useRegions = ({
	mapRefOl,
	areaPolygon,
	selectedPolygonId,
	filter,
	clickOnPolygon,
	isWarningPage,
}: {
	isWarningPage: boolean;
	mapRefOl: RefObject<MapOlType | null>;
	markerLayerRef: RefObject<VectorLayer | null>;
	areaPolygon?: AreaPolygonType;
	selectedPolygonId?: string;
	filter?: keyof DataWeatherStation;
	clickOnPolygon?: (open: boolean, value?: string) => void;
}) => {
	const regionsLayerRef = useRef<VectorLayer | null>(null);

	useEffect(() => {
		if (!mapRefOl.current) return;

		if (regionsLayerRef.current) {
			mapRefOl.current.removeLayer(regionsLayerRef.current);
			regionsLayerRef.current = null;
		}

		if (areaPolygon?.length === 0) return;

		const polyFeature = featuresPolygons(areaPolygon, selectedPolygonId) ?? [];

		if (!polyFeature) return;

		const vectorSource = new VectorSource({ features: polyFeature });

		const vectorLayer = new VectorLayer({
			source: vectorSource,
			zIndex: 900,
		});

		mapRefOl?.current?.addLayer(vectorLayer);
		regionsLayerRef.current = vectorLayer;
	}, [filter, areaPolygon, selectedPolygonId]);

	useEffect(() => {
		if (!mapRefOl.current) {
			return;
		}

		const map = mapRefOl.current;

		const handleClick = (
			event: MapBrowserEvent<PointerEvent | KeyboardEvent | WheelEvent>,
		) => {
			if (!isWarningPage) return;
			let hasClickedPolygon = false;

			map.forEachFeatureAtPixel(
				event.pixel,
				(feature) => {
					const featureId = feature.getId();

					if (featureId == null) return;

					const id = String(featureId);

					if (id === selectedPolygonId) {
						clickOnPolygon?.(false, undefined);
					} else {
						clickOnPolygon?.(true, id);
					}

					hasClickedPolygon = true;

					return true;
				},
				{
					layerFilter: (layer) => layer === regionsLayerRef.current,
				},
			);

			if (!hasClickedPolygon) {
				clickOnPolygon?.(false, undefined);
			}
		};

		map.on("singleclick", handleClick);

		return () => {
			map.un("singleclick", handleClick);
		};
	}, [selectedPolygonId, clickOnPolygon, isWarningPage]);
};
